import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link, Navigate } from "react-router-dom";
import { FiKey, FiShield, FiShieldOff, FiSmartphone, FiTrash2 } from "react-icons/fi";
import useAuth from "../../../hooks/useAuth";
import { actsAsAgencyAdmin } from "../../../utils/permissions";
import { repoUserService } from "../../../services/repoUser.service";
import { formatRepoRole, isManageableRepoUser } from "../../../constants/repoRoles";
import ResetPasswordModal from "../../../components/common/ResetPasswordModal";
import BlockCompanyModal from "../../../components/common/BlockCompanyModal";
import "../../../styles/users.css";
import "../../../styles/profile.css";

function IdentityDocumentView({ userId, kind, uploaded, token, refreshKey, onReplaced }) {
  const [src, setSrc] = useState("");
  const [isPdf, setIsPdf] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [replaceError, setReplaceError] = useState("");

  useEffect(() => {
    if (!uploaded || !userId || !token) {
      setSrc("");
      setIsPdf(false);
      setLoadError("");
      return undefined;
    }

    let objectUrl = "";
    let cancelled = false;

    repoUserService
      .getUserIdentityDocument(userId, kind, token)
      .then((blob) => {
        if (cancelled || !blob) return;
        if (blob.type && blob.type.includes("application/json")) {
          throw new Error("Unable to load document");
        }
        objectUrl = URL.createObjectURL(blob);
        setIsPdf(String(blob.type || "").includes("pdf"));
        setSrc(objectUrl);
        setLoadError("");
      })
      .catch(() => {
        if (!cancelled) {
          setSrc("");
          setIsPdf(false);
          setLoadError("Unable to load document");
        }
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [userId, kind, uploaded, token, refreshKey]);

  const handleReplace = async () => {
    if (!file || !userId || !token || saving) return;
    setSaving(true);
    setReplaceError("");
    try {
      const payload = new FormData();
      payload.append(kind === "pan" ? "panDocument" : "aadhaarDocument", file);
      await repoUserService.replaceUserIdentityDocuments(userId, payload, token);
      setFile(null);
      if (onReplaced) await onReplaced();
    } catch (err) {
      setReplaceError(
        err?.response?.data?.message || "Failed to upload document"
      );
    } finally {
      setSaving(false);
    }
  };

  const replaceForm = (
    <div className="identity-upload" style={{ marginTop: 8 }}>
      <label htmlFor={`replace-${kind}-document`}>
        {loadError || !uploaded ? "Upload document" : "Replace document"}
      </label>
      <input
        id={`replace-${kind}-document`}
        type="file"
        accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
        onChange={(e) => setFile(e.target.files?.[0] || null)}
      />
      {file ? (
        <button
          type="button"
          className="secondary-page-btn"
          onClick={handleReplace}
          disabled={saving}
        >
          {saving ? "Saving…" : "Save document"}
        </button>
      ) : (
        <p className="field-hint muted">JPG, JPEG, PNG, or PDF</p>
      )}
      {replaceError ? <p className="error-text">{replaceError}</p> : null}
    </div>
  );

  if (!uploaded) {
    return (
      <>
        <p className="muted tax-doc-upload__empty">No document uploaded</p>
        {replaceForm}
      </>
    );
  }
  if (loadError) {
    return (
      <>
        <p className="error-text">{loadError}. Upload the file again to store it on the server.</p>
        {replaceForm}
      </>
    );
  }
  if (!src) {
    return <p className="muted tax-doc-upload__empty">Loading document…</p>;
  }

  return (
    <div className="tax-doc-upload">
      {!isPdf ? (
        <button
          type="button"
          className="tax-doc-upload__preview--clickable"
          onClick={() => window.open(src, "_blank", "noopener,noreferrer")}
          aria-label="Open full-size document"
          style={{ padding: 0, background: "none" }}
        >
          <img className="tax-doc-upload__preview" src={src} alt="" />
        </button>
      ) : null}
      <button
        type="button"
        className="secondary-page-btn"
        onClick={() => window.open(src, "_blank", "noopener,noreferrer")}
      >
        View full size
      </button>
      {replaceForm}
    </div>
  );
}

export default function UserDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { auth } = useAuth();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const [resetError, setResetError] = useState("");
  const [resetFlash, setResetFlash] = useState("");
  const [error, setError] = useState("");
  const [blockOpen, setBlockOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [identityTick, setIdentityTick] = useState(0);

  const loadUser = useCallback(async () => {
    if (!id || !auth?.token) return;

    try {
      setLoading(true);
      setError("");

      const res = await repoUserService.getUserById(id, auth.token);
      const loaded = res?.data || res?.user || null;
      setUser(loaded);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load user details");
    } finally {
      setLoading(false);
    }
  }, [id, auth?.token]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const handleToggleStatus = async (blockReason = "") => {
    if (!user || statusUpdating) return;

    const userId = user._id || user.id;
    const nextStatus = !user.isActive;
    const previous = {
      isActive: user.isActive,
      blockedAt: user.blockedAt,
      blockReason: user.blockReason,
    };

    setStatusUpdating(true);
    setUser((prev) => ({
      ...prev,
      isActive: nextStatus,
      blockedAt: nextStatus ? null : new Date().toISOString(),
      blockReason: nextStatus ? "" : blockReason,
    }));

    try {
      await repoUserService.changeUserStatus(
        userId,
        nextStatus,
        auth.token,
        nextStatus ? {} : { blockReason: blockReason || "Blocked by admin" }
      );
      await loadUser();
    } catch (err) {
      setUser((prev) => ({ ...prev, ...previous }));
      alert(err?.response?.data?.message || "Failed to update user status");
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!user || deleting) return;
    const targetId = user._id || user.id;
    if (
      !window.confirm(
        `Delete "${user.name || "this agent"}"? This cannot be undone.`
      )
    ) {
      return;
    }
    setDeleting(true);
    try {
      await repoUserService.deleteUser(targetId, auth.token);
      navigate("/users");
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to delete agent");
    } finally {
      setDeleting(false);
    }
  };

  const handleResetPassword = () => {
    if (!user) return;
    setResetError("");
    setResetFlash("");
    setResetOpen(true);
  };

  const closeResetModal = () => {
    if (resetSubmitting) return;
    setResetOpen(false);
    setResetError("");
  };

  const confirmResetPassword = async (newPassword) => {
    if (!user) return;
    const userId = user._id || user.id;
    setResetSubmitting(true);
    setResetError("");
    try {
      await repoUserService.resetUserPassword(userId, newPassword, auth.token);
      setResetOpen(false);
      setResetFlash("Password reset successful");
    } catch (err) {
      setResetError(err?.response?.data?.message || "Failed to reset password");
    } finally {
      setResetSubmitting(false);
    }
  };

  if (loading) {
    return <div className="page">Loading user details...</div>;
  }

  if (error) {
    return (
      <div className="page">
        <p className="error-text">{error}</p>
        <button
          type="button"
          className="secondary-page-btn"
          onClick={() => navigate("/users")}
        >
          Back to Users
        </button>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="page">
        <p>User not found.</p>
        <button
          type="button"
          className="secondary-page-btn"
          onClick={() => navigate("/users")}
        >
          Back to Users
        </button>
      </div>
    );
  }

  const userId = user._id || user.id;
  const isTargetAdmin = user.role === "REPO_ADMIN";
  const isRepoAdmin = actsAsAgencyAdmin(auth);
  const isSelf = String(userId) === String(auth?.user?.id || auth?.user?._id);
  const canManage = isRepoAdmin && isManageableRepoUser(user.role);

  if (isSelf && !isRepoAdmin) {
    return <Navigate to="/profile" replace />;
  }

  return (
    <div className="page create-company-page profile-page">
      <div className="create-company-header">
        <div>
          <h2>User Details</h2>
          {isSelf && (
            <p className="muted">
              This is your account. You can also update mobile from{" "}
              <Link to="/profile">Profile</Link>.
            </p>
          )}
        </div>

        <button
          type="button"
          className="secondary-page-btn"
          onClick={() => navigate("/users")}
        >
          Back to Users
        </button>
      </div>

      {resetFlash && (
        <p className="reset-password-flash" role="status">
          {resetFlash}
        </p>
      )}

      <div className="create-company-form">
        <div className="create-company-card">
          <section className="create-company-section" aria-labelledby="user-details-head">
            <header className="create-company-section__head users-actions" id="user-details-head">
              <h3 style={{ margin: 0 }}>Profile information</h3>
              <span
                className={`company-status company-status--${
                  user.isActive ? "active" : "inactive"
                }`}
              >
                {user.isActive ? "Active" : user.blockedAt ? "Blocked" : "Inactive"}
              </span>
            </header>

            <div className="form-grid two-column">
            <div className="form-group">
              <label>Full Name</label>
              <input value={user.name || "-"} readOnly />
            </div>

            <div className="form-group">
              <label>Father&apos;s Name</label>
              <input value={user.fatherName || "-"} readOnly />
            </div>

            <div className="form-group">
              <label>Email</label>
              <input value={user.email || "-"} readOnly />
            </div>

            <div className="form-group">
              <label>
                <FiSmartphone aria-hidden style={{ marginRight: 6 }} />
                Mobile number
              </label>
              <input value={user.phone || "-"} readOnly />
              {user.phone ? (
                <a href={`tel:${user.phone}`} className="field-hint">
                  Call user
                </a>
              ) : null}
            </div>

            <div className="form-group">
              <label>Role</label>
              <input value={user.customRoleName || formatRepoRole(user.role)} readOnly />
            </div>

            {(user.panNumber ||
              user.aadhaarNumber ||
              user.panDocumentUploaded ||
              user.aadhaarDocumentUploaded) && (
              <>
                <div className="form-group">
                  <label>PAN Number</label>
                  <input value={user.panNumber || "-"} readOnly />
                  <label>PAN Card</label>
                  <IdentityDocumentView
                    userId={userId}
                    kind="pan"
                    uploaded={Boolean(user.panDocumentUploaded)}
                    token={auth?.token}
                    refreshKey={identityTick}
                    onReplaced={async () => {
                      await loadUser();
                      setIdentityTick((n) => n + 1);
                    }}
                  />
                </div>
                <div className="form-group">
                  <label>Aadhaar Number</label>
                  <input value={user.aadhaarNumber || "-"} readOnly />
                  <label>Aadhaar Card</label>
                  <IdentityDocumentView
                    userId={userId}
                    kind="aadhaar"
                    uploaded={Boolean(user.aadhaarDocumentUploaded)}
                    token={auth?.token}
                    refreshKey={identityTick}
                    onReplaced={async () => {
                      await loadUser();
                      setIdentityTick((n) => n + 1);
                    }}
                  />
                </div>
              </>
            )}

            <div className="form-group">
              <label>Blood Group</label>
              <span
                className={`blood-group-badge${user.bloodGroup ? "" : " empty"}`}
              >
                {user.bloodGroup || "-"}
              </span>
            </div>

            <div className="form-group full-width">
              <label>Address</label>
              <input value={user.address || "-"} readOnly />
            </div>

            <div className="form-group">
              <label>City</label>
              <input value={user.city || "-"} readOnly />
            </div>

            <div className="form-group">
              <label>State</label>
              <input value={user.state || "-"} readOnly />
            </div>

            <div className="form-group">
              <label>Pincode</label>
              <input value={user.pincode || "-"} readOnly />
            </div>

            <div className="form-group">
              <label>Account Status</label>
              <input
                value={
                  user.isActive ? "Active" : user.blockedAt ? "Blocked" : "Inactive"
                }
                readOnly
              />
            </div>

            <div className="form-group">
              <label>User ID</label>
              <input value={userId || "-"} readOnly className="input-readonly" />
            </div>
          </div>
          </section>
        </div>
        {(canManage || (isRepoAdmin && isSelf)) && (
          <div className="create-company-actions">
            {canManage && (
              <button
                type="button"
                className={`company-btn ${
                  user.isActive ? "company-btn--block" : "company-btn--unblock"
                }`}
                disabled={statusUpdating}
                onClick={() => {
                  if (user.isActive) setBlockOpen(true);
                  else handleToggleStatus();
                }}
              >
                {user.isActive ? (
                  <FiShieldOff aria-hidden />
                ) : (
                  <FiShield aria-hidden />
                )}
                <span>
                  {statusUpdating
                    ? "Updating..."
                    : user.isActive
                      ? "Block User"
                      : "Unblock User"}
                </span>
              </button>
            )}

            {isRepoAdmin && !isTargetAdmin && (
              <button
                type="button"
                className="company-btn company-btn--reset"
                onClick={handleResetPassword}
              >
                <FiKey aria-hidden />
                <span>Reset Password</span>
              </button>
            )}
            {canManage && (
              <button
                type="button"
                className="company-btn company-btn--delete"
                disabled={deleting}
                onClick={handleDeleteUser}
              >
                <FiTrash2 aria-hidden />
                <span>{deleting ? "Deleting…" : "Delete Agent"}</span>
              </button>
            )}
          </div>
        )}
      </div>
      <ResetPasswordModal
        open={resetOpen}
        subjectName={user?.name || "this user"}
        loading={resetSubmitting}
        error={resetError}
        onCancel={closeResetModal}
        onConfirm={confirmResetPassword}
      />
      <BlockCompanyModal
        open={blockOpen}
        companyName={user?.name}
        entityLabel="user"
        loading={statusUpdating}
        onCancel={() => {
          if (!statusUpdating) setBlockOpen(false);
        }}
        onConfirm={async (reason) => {
          setBlockOpen(false);
          await handleToggleStatus(reason);
        }}
      />
    </div>
  );
}
